import type { MatterRead } from '@/matter/store/schemas'
import { MatterType, MatterViewerRole } from '@/matter/store/schemas'

type MatterSubject = Pick<MatterRead, 'viewer_role'> | null | undefined
type MatterMembership = (Pick<MatterRead, 'viewer_role'> & Partial<Pick<MatterRead, 'accepted_at' | 'type'>>) | null | undefined

/** Can edit the matter. */
export function canEditMatter(matter: MatterSubject): boolean {
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Can delete the matter. */
export function canDeleteMatter(matter: MatterSubject): boolean {
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Whether the viewer may access this matter's DMS settings. */
export function canEditMatterSettings(matter: MatterMembership): boolean {
  return matter?.type !== MatterType.OUTLOOK_WORKSPACE && isActiveMatterMember(matter)
}

/** Can add/remove members and change roles. */
export function canEditMatterMembers(matter: MatterSubject): boolean {
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Ownership transfer is available only to the owner of an ordinary matter. */
export function canTransferMatterOwnership(matter: Pick<MatterRead, 'viewer_role' | 'type'> | null | undefined): boolean {
  return matter?.viewer_role === MatterViewerRole.Owner && matter.type === MatterType.STANDARD
}

/** Can view the members list. */
export function canViewMatterMembers(matter: MatterMembership): boolean {
  return isActiveMatterMember(matter)
}

/** Can create a new AI Assistant chat. */
export function canCreateChat(matter: MatterMembership): boolean {
  return isActiveMatterMember(matter)
}

/** Can view AI Assistant chat. */
export function canViewChats(matter: MatterMembership): boolean {
  return isActiveMatterMember(matter)
}

/** Whether the viewer can access the matter's Medical Chronology. */
export function canViewMedChron(matter: MatterMembership): boolean {
  return isActiveMatterMember(matter)
}

/**
 * Whether the viewer can edit the matter's Medical Chronology.
 * Missing matter data must keep write controls disabled until membership is known.
 */
export function canEditMedChron(matter: MatterMembership, editorEnabled = false): boolean {
  return matter?.viewer_role === MatterViewerRole.Owner || (editorEnabled && matter?.viewer_role === MatterViewerRole.Editor && isActiveMatterMember(matter))
}

/** Whether the viewer can access the matter's Timeline. */
export function canViewTimeline(matter: MatterMembership): boolean {
  return isActiveMatterMember(matter)
}

/** Whether the viewer can edit the matter's Timeline. */
export function canEditTimeline(matter: MatterMembership, editorEnabled = false): boolean {
  return matter?.viewer_role === MatterViewerRole.Owner || (editorEnabled && matter?.viewer_role === MatterViewerRole.Editor && isActiveMatterMember(matter))
}

/** Whether the viewer can access the matter's Medical Bills. */
export function canViewMedicalBills(matter: MatterMembership): boolean {
  return isActiveMatterMember(matter)
}

/** Whether the viewer can edit the matter's Medical Bills. */
export function canEditMedicalBills(matter: MatterSubject): boolean {
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Can move matter files to a different matter. */
export function canMoveMatterFilesExternally(matter: MatterSubject): boolean {
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Whether the viewer owns the matter or has accepted a Contributor or Editor invitation. */
export function isActiveMatterMember(matter: MatterMembership): boolean {
  if (matter?.viewer_role === MatterViewerRole.Owner) return true
  return (matter?.viewer_role === MatterViewerRole.Collaborator || matter?.viewer_role === MatterViewerRole.Editor) && matter.accepted_at != null
}
